import { useMatches, useNavigate, useParams } from 'react-router';
import { LogOut, Search, Bell, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '@/features/auth/use-auth';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TenantSwitcher } from './tenant-switcher';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super administrador',
  admin: 'Administradora',
  comite: 'Comité',
  conserje: 'Conserjería',
  propietario: 'Propietario',
  residente: 'Residente',
};

interface RouteHandle {
  breadcrumb?: string;
  pageTitle?: string;
}

function useBreadcrumb(): string[] {
  const matches = useMatches();
  return matches
    .map((m) => (m.handle as RouteHandle | undefined)?.breadcrumb)
    .filter((b): b is string => Boolean(b));
}

function HabitiaMark({
  size = 28,
  variant = 'teal',
}: {
  size?: number;
  variant?: 'teal' | 'dark' | 'mono';
}) {
  const bg = variant === 'teal' ? '#0E6B7A' : '#0A2F3E';
  const beam = variant === 'mono' ? 'white' : '#C98A2E';
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden>
      <rect width="80" height="80" rx="18" fill={bg} />
      <rect x="17" y="18" width="12" height="44" rx="2" fill="white" opacity="0.95" />
      <rect x="51" y="18" width="12" height="44" rx="2" fill="white" opacity="0.95" />
      <rect x="17" y="34" width="46" height="12" rx="2" fill={beam} opacity="0.92" />
    </svg>
  );
}

export function Topbar() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const breadcrumb = useBreadcrumb();
  // condoId is present when inside /app/condominios/:condoId/* routes.
  const { condoId } = useParams<{ condoId?: string }>();

  if (!session) return null;

  const effectiveRolLabel = (() => {
    if (session.isSuperAdmin) return ROLE_LABEL['super_admin'];
    if (condoId) {
      const r = session.tenants[condoId];
      return r ? (ROLE_LABEL[r] ?? r) : undefined;
    }
    return undefined;
  })();

  const initials = session.nombre
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-6 border-b border-[var(--color-hairline)] bg-[color-mix(in_oklch,var(--color-paper)_92%,transparent)] px-6 backdrop-blur-md">
      {/* Masthead: logo + wordmark */}
      <div className="flex items-center gap-3">
        <HabitiaMark size={28} />
        <div className="hidden items-baseline gap-1 font-display text-xl font-bold leading-none tracking-tight sm:flex">
          <span className="text-[var(--color-ink)]">
            Habit<span className="text-[var(--color-accent)]">ia</span>
          </span>
        </div>
      </div>

      {/* Tenant switcher — only visible inside a tenant route */}
      {condoId && <TenantSwitcher />}

      {/* Breadcrumb — driven by route handle metadata */}
      {breadcrumb.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="hidden min-w-0 flex-1 items-center gap-2 text-[0.8125rem] text-[var(--color-ink-muted)] md:flex"
        >
          {breadcrumb.map((crumb, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="text-[var(--color-ink-faint)]">›</span>}
              <span
                className={i === breadcrumb.length - 1 ? 'text-[var(--color-ink)] font-medium' : ''}
              >
                {crumb}
              </span>
            </span>
          ))}
        </nav>
      )}

      {/* Search */}
      <div className="hidden flex-1 items-center md:flex md:max-w-xs">
        <div className="group relative w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-ink-faint)] transition-colors group-focus-within:text-[var(--color-primary)]" />
          <input
            type="search"
            placeholder="Buscar residente, unidad o gasto..."
            className="h-9 w-full rounded border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] pl-9 pr-3 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] transition-colors focus:border-[var(--color-primary)] focus:bg-[var(--color-surface-white)] focus:outline-none"
            aria-label="Buscar"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded-sm border border-[var(--color-hairline-strong)] bg-[var(--color-surface-white)] px-1.5 py-0.5 font-mono text-[0.625rem] text-[var(--color-ink-muted)] lg:inline-flex">
            <span className="text-[0.6rem]">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Notifications + user menu */}
      <div className="ml-auto flex items-center gap-2">
        <Button variant="ghost" size="icon" aria-label="Notificaciones" className="relative">
          <Bell className="h-4 w-4" />
          <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded px-2 py-1.5 text-left transition-colors hover:bg-[var(--color-paper-dim)]"
            >
              <Avatar>
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden flex-col leading-tight sm:flex">
                <span className="text-sm font-semibold text-[var(--color-ink)]">
                  {session.nombre}
                </span>
                <span className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                  {session.email}
                </span>
              </div>
              <ChevronsUpDown className="hidden h-3.5 w-3.5 text-[var(--color-ink-faint)] sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Sesión activa</DropdownMenuLabel>
            <div className="px-2 py-1.5 text-xs text-[var(--color-ink-muted)]">
              {effectiveRolLabel ?? ''}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                void signOut().finally(() => {
                  navigate('/login', { replace: true });
                });
              }}
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
